// Line-art icons used across the slot shell.
// Stroke-based, 24x24 viewBox. Uses `currentColor` so they inherit text or accent color.
import type { CSSProperties, ReactNode } from 'react';

export interface IconProps {
  size?: number;
  style?: CSSProperties;
}

interface BaseIconProps extends IconProps {
  d: ReactNode;
  stroke?: number;
  fill?: string;
}

export function Icon({
  d,
  size = 22,
  stroke = 2.4,
  fill = 'none',
  style,
}: BaseIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
    >
      {d}
    </svg>
  );
}

export const IconSpin = ({ size = 22, style }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 63 63" fill="none" style={style} aria-hidden>
    <path d="M60.3283 27.7871C61.1383 27.7871 61.8197 28.4039 61.8581 29.213C61.8808 29.6929 61.8926 30.1757 61.8926 30.6611L61.8828 31.4531C61.4629 48.021 47.8995 61.3231 31.2305 61.3232C31.0644 61.321 30.9613 61.1416 31.0432 60.9971L35.1695 53.7055C35.3965 53.3045 35.7897 53.0264 36.2394 52.9257C46.4318 50.6426 54.0488 41.5423 54.0488 30.6611C54.0488 30.2914 54.04 29.9236 54.0225 29.5582C53.9774 28.6153 54.7033 27.7871 55.6473 27.7871H60.3283ZM9.69367 20.4635C10.1467 20.9264 10.2638 21.6182 10.0241 22.2199C8.98389 24.8309 8.41216 27.6792 8.41211 30.6611C8.41213 41.2056 15.5645 50.0779 25.2824 52.6946C25.999 52.8876 26.5215 53.5227 26.5215 54.2649V59.1339C26.5215 60.0955 25.6621 60.8333 24.7222 60.6301C10.9147 57.6455 0.56936 45.3617 0.569336 30.6611C0.569398 25.8134 1.69461 21.2287 3.69788 17.1534C4.15573 16.222 5.38964 16.0663 6.11561 16.808L9.69367 20.4635ZM31.2305 0C45.1835 0.000150229 56.9582 9.32059 60.6715 22.075C61.0439 23.354 59.6748 24.3325 58.5219 23.665L51.4083 19.5465C51.169 19.4079 50.9715 19.2078 50.8297 18.9705C46.8458 12.3058 39.5595 7.84289 31.2305 7.84277C25.4316 7.84287 20.1374 10.0059 16.1118 13.5694C15.6968 13.9367 15.1268 14.0834 14.5931 13.9343L9.29116 12.453C8.21631 12.1527 7.79394 10.8595 8.54497 10.034C14.1534 3.86965 22.2401 0.000114933 31.2305 0Z" fill="#FE37B4"/>
    <path d="M19.2246 29.0936L6.41551 16.5038C5.44892 15.5537 3.81366 16.194 3.74911 17.5478L3.30681 26.8239C3.2712 27.5707 3.76771 28.2389 4.49311 28.4203L17.7445 31.7341C19.2949 32.1218 20.3643 30.2138 19.2246 29.0936Z" fill="#FE37B4"/>
    <path d="M41.3937 18.8908L58.6698 23.8011C59.9735 24.1716 61.1018 22.8259 60.5097 21.6068L56.4519 13.2536C56.1252 12.581 55.3692 12.2325 54.6456 12.421L41.4272 15.8639C39.8807 16.2667 39.8565 18.4538 41.3937 18.8908Z" fill="#FE37B4"/>
    <path d="M38.4713 43.9671L29.77 59.6789C29.1134 60.8646 30.168 62.2688 31.4897 61.9685L40.5456 59.9115C41.2748 59.7459 41.7856 59.0886 41.7662 58.3412L41.4117 44.6863C41.3702 43.0887 39.2456 42.569 38.4713 43.9671Z" fill="#FE37B4"/>
  </svg>
);

export const IconAuto = ({ size = 22, style }: IconProps) => (
  <svg
    width={size}
    height={size * (44 / 37)}
    viewBox="0 0 37 44"
    fill="currentColor"
    style={style}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18.4119 1.14351V7.50476C29.8835 7.50476 38.94 17.7192 36.641 29.4157C35.2245 36.5294 29.3959 42.2522 22.1507 43.643C11.1668 45.7407 1.46004 38.3306 0.0202934 28.3213C-0.165481 26.9533 0.949165 25.7449 2.34247 25.7449C3.48034 25.7449 4.45565 26.5885 4.61821 27.6829C5.73285 35.2298 13.1174 40.8158 21.4076 39.1058C26.6557 38.0342 30.9285 33.8618 32.0199 28.6861C33.9241 19.8625 27.0737 12.0648 18.4119 12.0648V18.426C18.4119 19.452 17.158 19.9536 16.4149 19.2468L7.61382 10.6056C7.14938 10.1496 7.14938 9.44277 7.61382 8.98677L16.4381 0.345501C17.158 -0.384104 18.4119 0.117499 18.4119 1.14351Z"
    />
  </svg>
);

export const IconStop = ({ size = 22, style }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    style={style}
  >
    <rect x="6" y="6" width="12" height="12" rx="2" />
  </svg>
);

export const IconTurbo = (p: IconProps) => (
  <Icon
    {...p}
    d={
      <polygon
        points="13 2 4 14 11 14 10 22 20 10 13 10 13 2"
        fill="currentColor"
        stroke="none"
      />
    }
  />
);

export const IconMenu = (p: IconProps) => (
  <Icon
    {...p}
    d={
      <>
        <line x1="4" y1="7" x2="20" y2="7" />
        <line x1="4" y1="12" x2="20" y2="12" />
        <line x1="4" y1="17" x2="20" y2="17" />
      </>
    }
  />
);

export const IconInfo = (p: IconProps) => (
  <Icon
    {...p}
    d={
      <>
        <circle cx="12" cy="12" r="9" />
        <line x1="12" y1="11" x2="12" y2="17" />
        <circle cx="12" cy="7.5" r="0.8" fill="currentColor" stroke="none" />
      </>
    }
  />
);

export const IconClose = (p: IconProps) => (
  <Icon
    {...p}
    d={
      <>
        <line x1="6" y1="6" x2="18" y2="18" />
        <line x1="18" y1="6" x2="6" y2="18" />
      </>
    }
  />
);

export const IconMinus = (p: IconProps) => (
  <Icon {...p} d={<line x1="5" y1="12" x2="19" y2="12" />} />
);

export const IconPlus = (p: IconProps) => (
  <Icon
    {...p}
    d={
      <>
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </>
    }
  />
);

export const IconSound = (p: IconProps) => (
  <Icon
    {...p}
    d={
      <>
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
        <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
      </>
    }
  />
);

export const IconMute = (p: IconProps) => (
  <Icon
    {...p}
    d={
      <>
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
        <line x1="22" y1="9" x2="16" y2="15" />
        <line x1="16" y1="9" x2="22" y2="15" />
      </>
    }
  />
);

export const IconBolt = (p: IconProps) => (
  <Icon {...p} d={<polygon points="13 2 4 14 11 14 10 22 20 10 13 10 13 2" />} />
);

export const IconChevronLeft = (p: IconProps) => (
  <Icon {...p} d={<polyline points="15 6 9 12 15 18" />} />
);
export const IconChevronRight = (p: IconProps) => (
  <Icon {...p} d={<polyline points="9 6 15 12 9 18" />} />
);

export const IconExternalLink = (p: IconProps) => (
  <Icon
    {...p}
    d={
      <>
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
        <polyline points="15 3 21 3 21 9" />
        <line x1="10" y1="14" x2="21" y2="3" />
      </>
    }
  />
);

export const IconWallet = ({ size = 22, style }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 39 39"
    fill="currentColor"
    style={style}
  >
    <path d="M22.8 15.8746C22.8 16.8009 22.0613 17.5519 21.15 17.5519C20.2387 17.5519 19.5 16.8009 19.5 15.8746C19.5 14.9482 20.2387 14.1973 21.15 14.1973C22.0613 14.1973 22.8 14.9482 22.8 15.8746Z" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M25.1934 3.13179C26.9458 2.62283 28.7723 3.65433 29.273 5.43571C29.3573 5.73541 29.4 6.04558 29.4 6.35727V9.16549H32.7C34.5225 9.16549 36 10.6674 36 12.52V32.6473C36 34.5 34.5225 36.0019 32.7 36.0019H6.3C4.47746 36.0019 3 34.5 3 32.6473H3.04406C3.01477 32.4669 3 32.2842 3 32.101V12.1079C3 10.6102 3.97672 9.29391 5.39342 8.88245L25.1934 3.13179ZM16.4311 32.6473H32.7V19.2291H29.4V26.3503C29.4 27.8481 28.4233 29.1643 27.0066 29.5758L16.4311 32.6473ZM29.4 12.52H32.7V15.8746H29.4V12.52ZM6.3 12.1079V32.1009L26.1 26.3503V6.35723L6.3 12.1079Z"
    />
  </svg>
);
